import type { FunctionComponent } from "react";
import { useEffect, useState } from "react";

import dayjs from "dayjs";

import { InfoCardApps } from "@similarweb/ui-components";

import { i18nFilter } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { AssetsService } from "app/services/AssetsService";
import { DefaultFetchService } from "app/services/fetchService";

interface IAppTooltipContentProps {
    app?: any;
    appId?: string;
    store?: string;
    hideMonthlyDownloads?: boolean;
}

const getImageSrc = (imgSrc: string, category: string) => {
    if (category === "Adult") {
        return AssetsService.assetUrl("/images/new-adult-tall-app.png");
    } else {
        return imgSrc || AssetsService.assetUrl("/images/new-no-image-app.png");
    }
};

const fetchService = DefaultFetchService.getInstance();

const getRatingNumber = (rating: number) => {
    if (!rating) {
        return;
    }
    return rating === 0 ? null : +rating.toFixed(1);
};

export const AppTooltipContent: FunctionComponent<IAppTooltipContentProps> = (props) => {
    const { appId, store, hideMonthlyDownloads = false } = props;
    const validStore = store === "0" ? "Google" : store === "1" ? "Apple" : store || "";

    const [data, setData] = useState<FixmeType>(null);
    useEffect(() => {
        const abortController = new AbortController();
        const fetchData = async () => {
            try {
                const response = await fetchService.get(
                    `/api/MobileApps/GetHeader?appId=${appId}&store=${validStore}&country=840`,
                    null,
                    {
                        preventAutoCancellation: false,
                        cancellation: abortController.signal,
                    },
                );
                setData(response);
            } catch (e) {}
        };
        fetchData();

        return function cleanup() {
            abortController.abort();
        };
    }, [appId, store]);

    if (data) {
        return (
            <InfoCardApps
                isLoadingData={false}
                appName={data.Name}
                icon={data.Icon ? data.Icon : AssetsService.assetUrl("/images/autocomplete-default.png")}
                description={data.Description ? data.Description : i18nFilter()("infotip.description.undefined")}
                store={
                    data.AppStore === "Google" ? "Google Play" : data.AppStore === "Apple" ? "App Store" : data.AppStore
                }
                price={data.Price}
                category={data.Category}
                rating={getRatingNumber(data.Rating)}
                imgSrc1={getImageSrc(data?.Screenshots?.[0], data.Category)}
                imgSrc2={getImageSrc(data?.Screenshots?.[1], data.Category)}
                countryId={data.Country ? data.Country : 999}
                categoryRankingLabel={i18nFilter()("infotip.app.categoryRank")}
                categoryRanking={data.BestMonthlyCategoryRank ? abbrNumberFilter()(data.BestMonthlyCategoryRank) : "-"}
                monthlyDownloadsLabel={i18nFilter()("infotip.app.monthlyDownloads")}
                monthlyDownloads={data.MonthlyDownloads ? abbrNumberFilter()(data.MonthlyDownloads) : "-"}
                hideMonthlyDownloads={hideMonthlyDownloads}
                lastVersionLabel={i18nFilter()("infotip.app.lastVersion")}
                lastVersion={data.LastVersion ? dayjs(data.LastVersion).format("MMM YYYY") : "-"}
            />
        );
    }
    return <InfoCardApps isLoadingData={true} />;
};

AppTooltipContent.displayName = "AppTooltipContent";
